import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getPendingCount } from '../../lib/db';
import { syncPendingAttendance, getSecurityAlertsStats } from '../../lib/api';
import { getRoleLabel } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  LayoutDashboard, Camera, Calendar, FileText, MapPin, 
  Building2, Users, Settings, LogOut, Menu, X, Wifi, WifiOff,
  RefreshCw, AlertCircle, UserCheck, Image, User, UsersRound, CalendarDays,
  ScanFace, Smartphone, Cog, ShieldAlert, ShieldCheck, BookOpen, Package, Upload,
  Landmark, ChevronDown, ChevronRight, ClipboardList
} from 'lucide-react';
import { toast } from '../ui/sonner';

const DashboardLayout = () => {
  const { user, logout, isOnline } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const [expandedMenus, setExpandedMenus] = useState({});

  useEffect(() => {
    loadPendingCount();
    loadAlertCount();
    const interval = setInterval(loadPendingCount, 10000); // Check every 10s
    const alertInterval = setInterval(loadAlertCount, 30000); // Check alerts every 30s
    return () => {
      clearInterval(interval);
      clearInterval(alertInterval);
    };
  }, []);

  useEffect(() => {
    // Auto-sync when coming online
    const autoSync = async () => {
      if (isOnline && pendingCount > 0) {
        console.log(`Auto-sync triggered: ${pendingCount} pending records`);
        await handleSync();
      }
    };
    
    autoSync();
    
    // Also add online event listener for immediate sync
    const handleOnline = async () => {
      console.log('Device came online, checking for pending sync...');
      const count = await getPendingCount();
      if (count > 0) {
        console.log(`Found ${count} pending records, syncing...`);
        await handleSync();
      }
    };
    
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [isOnline, pendingCount]);

  const loadPendingCount = async () => {
    const count = await getPendingCount();
    setPendingCount(count);
  };

  const loadAlertCount = async () => {
    // Only load for admin/kepala_sekolah/pimpinan
    if (!user || !['admin_dinas', 'kepala_sekolah', 'pimpinan'].includes(user.role)) {
      return;
    }
    try {
      const stats = await getSecurityAlertsStats();
      setAlertCount(stats.total_pending || 0);
    } catch (err) {
      console.error('Failed to load alert count:', err);
    }
  };

  const handleSync = async () => {
    if (!isOnline || syncing) return;
    
    setSyncing(true);
    try {
      const result = await syncPendingAttendance();
      if (result.synced > 0) {
        toast.success(`${result.synced} data berhasil disinkronkan`);
        await loadPendingCount();
      }
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setSyncing(false);
    }
  };

  const handleLogout = () => {
    // Warn user if offline
    if (!isOnline) {
      const confirm = window.confirm(
        'Anda sedang offline. Jika logout sekarang, Anda tidak akan bisa login kembali tanpa koneksi internet.\n\nLanjutkan logout?'
      );
      if (!confirm) return;
    }
    logout();
    navigate('/login');
  };

  // Navigation items based on role
  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', roles: ['guru', 'kepala_sekolah', 'admin_dinas', 'pimpinan', 'editor', 'pengawas', 'tenaga_tata_usaha', 'pegawai_instansi'] },
    { icon: Camera, label: 'Absensi', path: '/absensi', roles: ['guru', 'kepala_sekolah', 'pengawas', 'tenaga_tata_usaha', 'pegawai_instansi', 'pimpinan', 'editor'] },
    { icon: Image, label: 'Foto Kegiatan', path: '/foto-kegiatan', roles: ['guru', 'kepala_sekolah', 'tenaga_tata_usaha'] },
    { icon: Calendar, label: 'Riwayat Absensi', path: '/riwayat', roles: ['guru', 'kepala_sekolah', 'tenaga_tata_usaha', 'pegawai_instansi', 'pimpinan', 'editor'] },
    { icon: AlertCircle, label: 'Izin', roles: ['guru', 'kepala_sekolah', 'admin_dinas', 'pimpinan', 'editor', 'pengawas', 'tenaga_tata_usaha', 'pegawai_instansi'], children: [
      { icon: Building2, label: 'Kelola Izin Sekolah', path: '/izin-sekolah', roles: ['kepala_sekolah', 'admin_dinas', 'pimpinan', 'editor', 'pengawas'] },
      { icon: Landmark, label: 'Kelola Izin Instansi', path: '/izin-instansi', roles: ['admin_dinas', 'pimpinan', 'editor'] },
      { icon: AlertCircle, label: 'Pengajuan Izin Saya', path: '/izin', roles: ['guru', 'tenaga_tata_usaha', 'pegawai_instansi'] },
    ]},
    { icon: FileText, label: 'Surat Tugas', path: '/surat-tugas', roles: ['guru', 'kepala_sekolah', 'admin_dinas', 'tenaga_tata_usaha', 'pegawai_instansi', 'pimpinan', 'editor', 'pengawas'] },
    { icon: ScanFace, label: 'Daftar Wajah', path: '/face-registration', roles: ['kepala_sekolah', 'admin_dinas'] },
    { icon: Smartphone, label: 'Mode Kiosk', path: '/kiosk-management', roles: ['kepala_sekolah', 'admin_dinas'] },
    { icon: CalendarDays, label: 'Hari Aktif', path: '/hari-aktif', roles: ['admin_dinas', 'pimpinan'] },
    { icon: FileText, label: 'Laporan', path: '/laporan', roles: ['kepala_sekolah', 'admin_dinas', 'pimpinan', 'editor', 'pengawas'] },
    { icon: MapPin, label: 'Peta', path: '/peta', roles: ['kepala_sekolah', 'admin_dinas', 'pimpinan', 'editor', 'pengawas'] },
    { icon: ShieldAlert, label: 'Security Alerts', path: '/security-alerts', roles: ['kepala_sekolah', 'admin_dinas', 'pimpinan', 'editor'], badge: alertCount },
    { icon: ShieldCheck, label: 'Laporan Liveness', path: '/liveness-report', roles: ['kepala_sekolah', 'admin_dinas', 'pimpinan'] },
    { icon: Building2, label: 'Sekolah', path: '/sekolah', roles: ['admin_dinas'] },
    { icon: Landmark, label: 'Instansi', path: '/instansi', roles: ['admin_dinas'] },
    { icon: FileText, label: 'Laporan Instansi', path: '/laporan-instansi', roles: ['admin_dinas', 'pimpinan'] },
    { icon: Users, label: 'Riwayat Absensi', roles: ['kepala_sekolah', 'admin_dinas', 'pimpinan', 'editor', 'pengawas'], children: [
      { icon: Building2, label: 'Riwayat Absensi Guru', path: '/riwayat-guru', roles: ['kepala_sekolah', 'admin_dinas', 'pimpinan', 'editor', 'pengawas'] },
      { icon: Landmark, label: 'Riwayat Absensi Instansi', path: '/riwayat-instansi', roles: ['admin_dinas', 'pimpinan'] },
    ]},
    { icon: UsersRound, label: 'Manajemen User', path: '/users', roles: ['admin_dinas', 'pimpinan', 'editor'] },
    { icon: Upload, label: 'Bulk Import', path: '/bulk-import', roles: ['admin_dinas'] },
    { icon: Cog, label: 'Pengaturan', path: '/pengaturan', roles: ['admin_dinas'] },
    { icon: Package, label: 'Kelola Versi', path: '/version-management', roles: ['admin_dinas'] },
    { icon: Settings, label: 'Audit Log', path: '/audit', roles: ['admin_dinas', 'pimpinan'] },
    { icon: User, label: 'Profil', path: '/profil', roles: ['guru', 'kepala_sekolah', 'admin_dinas', 'pimpinan', 'editor', 'pengawas', 'tenaga_tata_usaha', 'pegawai_instansi'] },
  ].filter(item => {
    if (item.children) {
      item.children = item.children.filter(child => child.roles.includes(user?.role));
      return item.children.length > 0 && item.roles.includes(user?.role);
    }
    return item.roles.includes(user?.role);
  });

  const isActive = (path) => location.pathname === path;
  const isChildActive = (item) => item.children?.some(child => location.pathname === child.path);

  const toggleSubmenu = (label) => {
    setExpandedMenus(prev => ({ ...prev, [label]: !prev[label] }));
  };

  // Close sidebar when clicking outside on mobile
  const closeSidebar = () => setSidebarOpen(false);

  // Check if user is pegawai_instansi for different branding
  const isPegawaiInstansi = user?.role === 'pegawai_instansi';
  const appName = isPegawaiInstansi ? 'SIAP INSTANSI' : 'SIGAP GURU';
  const appTagline = isPegawaiInstansi ? 'Sistem Kehadiran Instansi' : 'Sistem Kehadiran Guru';

  return (
    <div className="min-h-screen bg-[#fcfbf9]">
      {/* Mobile Header */}
      <header className={`lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b ${isPegawaiInstansi ? 'border-slate-200' : 'border-indigo-100'} px-4 py-3`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setSidebarOpen(true)}
              className={isPegawaiInstansi ? 'hover:bg-slate-50' : 'hover:bg-indigo-50'}
            >
              <Menu className={`w-5 h-5 ${isPegawaiInstansi ? 'text-slate-800' : 'text-indigo-950'}`} />
            </Button>
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isPegawaiInstansi ? 'bg-slate-700' : 'bg-indigo-950'}`}>
                <UserCheck className={`w-4 h-4 ${isPegawaiInstansi ? 'text-blue-300' : 'text-amber-500'}`} />
              </div>
              <span className={`font-bold ${isPegawaiInstansi ? 'text-slate-800' : 'text-indigo-950'}`}>{appName}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {pendingCount > 0 && (
              <Badge 
                className="bg-amber-100 text-amber-800 cursor-pointer"
                onClick={handleSync}
              >
                {syncing ? <RefreshCw className="w-3 h-3 animate-spin" /> : pendingCount}
              </Badge>
            )}
            {isOnline ? (
              <Wifi className="w-4 h-4 text-emerald-500" />
            ) : (
              <WifiOff className="w-4 h-4 text-red-500" />
            )}
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      <div 
        className={`lg:hidden fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${
          sidebarOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        onClick={closeSidebar}
      />

      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 h-full w-64 text-white z-50
          transform transition-transform duration-300 ease-in-out
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isPegawaiInstansi ? 'bg-slate-800' : 'bg-indigo-950'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`p-6 border-b ${isPegawaiInstansi ? 'border-slate-700/50' : 'border-indigo-800/50'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isPegawaiInstansi ? 'bg-blue-500' : 'bg-amber-500'}`}>
                  <UserCheck className={`w-6 h-6 ${isPegawaiInstansi ? 'text-white' : 'text-indigo-950'}`} />
                </div>
                <div>
                  <h1 className="font-bold text-lg text-white">{appName}</h1>
                  <p className={`text-xs ${isPegawaiInstansi ? 'text-slate-400' : 'text-indigo-300'}`}>{appTagline}</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                className={`lg:hidden text-white ${isPegawaiInstansi ? 'hover:bg-slate-700' : 'hover:bg-indigo-800'}`}
                onClick={closeSidebar}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Online Status */}
          <div className={`px-4 py-3 border-b ${isPegawaiInstansi ? 'border-slate-700/50' : 'border-indigo-800/50'}`}>
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                {isOnline ? (
                  <>
                    <div className="status-online" />
                    <span className="text-emerald-400">Online</span>
                  </>
                ) : (
                  <>
                    <div className="status-offline" />
                    <span className="text-red-400">Offline</span>
                  </>
                )}
              </div>
              {pendingCount > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-amber-400 hover:text-amber-300 hover:bg-indigo-800 text-xs"
                  onClick={handleSync}
                  disabled={!isOnline || syncing}
                >
                  {syncing ? (
                    <RefreshCw className="w-3 h-3 animate-spin mr-1" />
                  ) : (
                    <RefreshCw className="w-3 h-3 mr-1" />
                  )}
                  {pendingCount} pending
                </Button>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-auto">
            {navItems.map((item) => (
              item.children ? (
                <div key={item.label}>
                  <button
                    onClick={() => toggleSubmenu(item.label)}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all
                      ${isChildActive(item)
                        ? isPegawaiInstansi
                          ? 'bg-slate-700 text-white font-medium'
                          : 'bg-indigo-800 text-white font-medium'
                        : isPegawaiInstansi
                          ? 'text-slate-300 hover:bg-slate-700 hover:text-white'
                          : 'text-indigo-200 hover:bg-indigo-800 hover:text-white'
                      }
                    `}
                    data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="flex-1">{item.label}</span>
                    {(expandedMenus[item.label] || isChildActive(item)) ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                  {(expandedMenus[item.label] || isChildActive(item)) && (
                    <div className="ml-4 mt-1 space-y-1">
                      {item.children.map((child) => (
                        <button
                          key={child.path}
                          onClick={() => {
                            navigate(child.path);
                            setSidebarOpen(false);
                          }}
                          className={`
                            w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-left transition-all text-sm
                            ${isActive(child.path)
                              ? isPegawaiInstansi
                                ? 'bg-blue-600 text-white font-medium'
                                : 'bg-amber-500 text-indigo-950 font-medium'
                              : isPegawaiInstansi
                                ? 'text-slate-400 hover:bg-slate-700 hover:text-white'
                                : 'text-indigo-300 hover:bg-indigo-800 hover:text-white'
                            }
                          `}
                          data-testid={`nav-${child.label.toLowerCase().replace(/ /g, '-')}`}
                        >
                          <child.icon className="w-4 h-4" />
                          <span className="flex-1">{child.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all
                  ${isActive(item.path) 
                    ? isPegawaiInstansi
                      ? 'bg-blue-600 text-white font-medium'
                      : 'bg-amber-500 text-indigo-950 font-medium' 
                    : isPegawaiInstansi
                      ? 'text-slate-300 hover:bg-slate-700 hover:text-white'
                      : 'text-indigo-200 hover:bg-indigo-800 hover:text-white'
                  }
                `}
                data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="flex-1">{item.label}</span>
                {item.badge > 0 && (
                  <Badge className="bg-red-500 text-white text-xs px-2 py-0.5 animate-pulse">
                    {item.badge}
                  </Badge>
                )}
              </button>
              )
            ))}

            {/* Panduan - always at bottom of nav */}
            <button
              onClick={() => { navigate('/panduan'); setSidebarOpen(false); }}
              className={`
                w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all mt-2 pt-4
                ${isPegawaiInstansi ? 'border-t border-slate-700/30' : 'border-t border-indigo-800/30'}
                ${isActive('/panduan') 
                  ? isPegawaiInstansi
                    ? 'bg-blue-600 text-white font-medium'
                    : 'bg-amber-500 text-indigo-950 font-medium' 
                  : isPegawaiInstansi
                    ? 'text-slate-300 hover:bg-slate-700 hover:text-white'
                    : 'text-indigo-200 hover:bg-indigo-800 hover:text-white'
                }
              `}
              data-testid="panduan-sidebar-btn"
            >
              <BookOpen className="w-5 h-5" />
              <span className="flex-1">Panduan</span>
            </button>
          </nav>

          {/* User Info & Logout */}
          <div className={`p-3 border-t ${isPegawaiInstansi ? 'border-slate-700/50' : 'border-indigo-800/50'}`}>
            <div className="flex items-center justify-between mb-2 px-2">
              <div>
                <p className="font-medium truncate text-sm">{user?.nama}</p>
                <p className="text-indigo-300 text-xs">{getRoleLabel(user?.role)}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-red-950/50"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Keluar
            </Button>
            
            {/* Footer */}
            <p className={`${isPegawaiInstansi ? 'text-slate-400' : 'text-indigo-400'} text-xs text-center mt-2 px-2`}>
              © 2025 {appName} - Dinas Pendidikan Kabupaten Asmat
            </p>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content pt-16 lg:pt-0">
        <Outlet />
        
        {/* Mobile Footer */}
        <footer className="lg:hidden py-4 text-center text-xs text-gray-400 border-t border-gray-100 mt-8">
          © 2025 {appName} Dinas Pendidikan Kabupaten Asmat
        </footer>
      </main>
    </div>
  );
};

export default DashboardLayout;
